import { IFailedTempPlannedPortfolio, IRebalancedPayload } from './../../../models/plannedPortfolios';
import { Store, select } from '@ngrx/store';
import { Injectable } from "@angular/core";
import { switchMap, map, catchError } from 'rxjs/operators';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, Subscription } from 'rxjs';
import { IPlannedPortfoliosState } from './../../state/planned-portfolios.state';
import { IAppState } from './../../state/app.state';
import { PlannedPortfoliosFacade } from './../facade/planned-portfolios.facade';
import { PlannedPortfoliosService } from './../../../services/http/planned-portfolios.service';
import { IPlannedPortfolioPayload, IPlannedPortfolio } from '../../../models/plannedPortfolios';
import * as PlannedPortfoliosActions from '../actions/planned-portfolios.actions';
import * as GlobalNotificationActions from './../../global-notification/actions/global-notification.action';
import { getPlannedPortfoliosState } from '../selectors/planned-portfolios.selectors';
import { ModelPortfolioutilsService } from 'src/app/services/utils/model-portfolio-utils.service';

@Injectable()
export class PlannedPortfoliosEffects {

    plannedPortfolioSubscription$: Subscription;
    plannedPortfoliosState: IPlannedPortfoliosState;

    constructor(private actions$: Actions, private plannedPortfoliosService: PlannedPortfoliosService, private modelPortfolioUtilsService: ModelPortfolioutilsService, private plannedModelPortfoliosFacade: PlannedPortfoliosFacade, private store: Store<IAppState>) {
        this.plannedPortfolioSubscription$ = this.store.pipe(select(getPlannedPortfoliosState)).subscribe((state: IPlannedPortfoliosState) => {
            this.plannedPortfoliosState = state;
        });
    }

    selectPlannedPortfolio$ = createEffect(() => this.actions$.pipe(
        ofType<PlannedPortfoliosActions.SelectPlannedPortfolio>(PlannedPortfoliosActions.PlannedPortfoliosActionTypes.SELECT_PLANNED_PORTFOLIO),
        switchMap((action: PlannedPortfoliosActions.SelectPlannedPortfolio) => {
            return this.plannedPortfoliosService.getPlannedPortfolio(<IPlannedPortfolioPayload>action.payload)
                .pipe(
                    switchMap((res: any) => {
                        if (res) {
                            const failedTempPlannedPortfolios: IFailedTempPlannedPortfolio[] = this.modelPortfolioUtilsService.getFailedTempPlannedPortfolios(res);
                            const selectedTempPlannedPortfolio = res[0];
                            const payload: IRebalancedPayload = {
                                rebalancedPlannedPortfolios: this.modelPortfolioUtilsService.addTempPlannedPortfolios(res),
                                selectedTempPlannedPortfolio: selectedTempPlannedPortfolio,
                                failedTempPlannedPortfolios: (failedTempPlannedPortfolios.length > 0) ? failedTempPlannedPortfolios : []
                            };
                            if (selectedTempPlannedPortfolio && selectedTempPlannedPortfolio.rebalancingErrorMessage !== null && selectedTempPlannedPortfolio.rebalancingErrorMessage !== "") {
                                return of(
                                    new PlannedPortfoliosActions.SelectPlannedPortfolioFail(payload)
                                );
                            } else {
                                return of(
                                    new PlannedPortfoliosActions.SelectPlannedPortfolioSuccess(payload)
                                );
                            }
                        }
                    })
                    , catchError((err) => {
                        return of(
                            new PlannedPortfoliosActions.PlannedPortfoliosDataError(err.error.message),
                            new GlobalNotificationActions.NotificationErrorAction(err.error.message)
                        );
                    })
                );
        })
    ));

    SelectTemporaryPlannedPortfolio$ = createEffect(() => this.actions$.pipe(
        ofType<PlannedPortfoliosActions.SelectTemporaryPlannedPortfolio>(PlannedPortfoliosActions.PlannedPortfoliosActionTypes.SELECT_TEMP_PLANNED_PORTFOLIO),
        switchMap((action: PlannedPortfoliosActions.SelectTemporaryPlannedPortfolio) => {
            return this.plannedPortfoliosService.getTemporaryPlannedPortfolio(<IPlannedPortfolio>action.payload)
                .pipe(
                    switchMap((res: IPlannedPortfolio[]) => {
                        if (res) {
                            const failedTempPlannedPortfolios: IFailedTempPlannedPortfolio[] = this.modelPortfolioUtilsService.getFailedTempPlannedPortfolios(res);
                            const selectedTempPlannedPortfolio = res[0];
                            const payload: IRebalancedPayload = {
                                rebalancedPlannedPortfolios: this.modelPortfolioUtilsService.addTempPlannedPortfolios(res),
                                selectedTempPlannedPortfolio: selectedTempPlannedPortfolio,
                                failedTempPlannedPortfolios: (failedTempPlannedPortfolios.length > 0) ? failedTempPlannedPortfolios : []
                            };
                            if (selectedTempPlannedPortfolio && selectedTempPlannedPortfolio.rebalancingErrorMessage !== null && selectedTempPlannedPortfolio.rebalancingErrorMessage !== "") {
                                return of(
                                    new PlannedPortfoliosActions.SelectTempPlannedPortfolioFail(payload),
                                    new GlobalNotificationActions.NotificationErrorAction('Model portfolio unsuccessfully applied')
                                );
                            } else {
                                return of(
                                    new PlannedPortfoliosActions.SelectTempPlannedPortfolioSuccess(payload),
                                    new GlobalNotificationActions.NotificationSuccessAction('Model portfolio successfully applied')
                                );
                            }
                        }
                    })
                    , catchError((err) => {
                        const errorMessage = (err.error.message) ? "Portfolio couldn't be rebalanced. " + err.error.message : "Rebalanced portfolio could not be retreived";
                        return of(
                            new PlannedPortfoliosActions.PlannedPortfoliosDataError(errorMessage),
                            new GlobalNotificationActions.NotificationErrorAction(errorMessage)
                        );
                    })
                );
        })
    ));

    submitPlannedPortfolio$ = createEffect(() => this.actions$.pipe(
        ofType<PlannedPortfoliosActions.SubmitPlannedPortfolio>(PlannedPortfoliosActions.PlannedPortfoliosActionTypes.SUBMIT_PLANNED_PORTFOLIO),
        switchMap((action: PlannedPortfoliosActions.SubmitPlannedPortfolio) => {
            return this.plannedPortfoliosService.savePlannedPortfolio(<IPlannedPortfolio>action.payload)
                .pipe(
                    switchMap((res: IPlannedPortfolio) => {
                        if (res) {
                            return of(
                                new PlannedPortfoliosActions.SubmitPlannedPortfolioSuccess(action.payload),
                                new GlobalNotificationActions.NotificationSuccessAction('Planned portfolio successfully saved')
                            );
                        }
                    })
                    , catchError((err) => {
                        return of(
                            new GlobalNotificationActions.NotificationErrorAction("Planned Portfolio could not be saved"),
                            new PlannedPortfoliosActions.PlannedPortfoliosDataError("Planned Portfolio could not be saved")
                        );
                    })
                );
        })
    ));

}
